import React, { useMemo } from "react";
import { Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import "./marketStrategyInsightsCard.css";

const AI_INSIGHT_POPOVER_CONTENT = {
  buyer_demand: {
    intro: "Measures how reliably buyers purchase this vehicle when priced correctly.",
    signalsUsed: ["Units sold in last 60-90 days", "Median sold days on market", "Sell-through vs active inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Strong demand" },
      { grades: "C", desc: "Selective demand" },
      { grades: "D–F", desc: "Weak or inconsistent" },
    ],
  },
  interest: {
    intro: "Measures how actively buyers are engaging with similar listings in your market.",
    signalsUsed: ["Active listing freshness", "Sold velocity", "Price alignment", "Momentum"],
    gradeMeaning: [
      { grades: "A–B", desc: "High interest" },
      { grades: "C", desc: "Moderate interest" },
      { grades: "D–F", desc: "Low interest" },
    ],
  },
  volume: {
    intro: "Measures how much real market activity exists to support reliable insights.",
    signalsUsed: ["Sold activity in window", "Active pool size", "Market liquidity"],
    gradeMeaning: [
      { grades: "A–B", desc: "High volume" },
      { grades: "C", desc: "Moderate volume" },
      { grades: "D–F", desc: "Low volume" },
    ],
  },
  availability: {
    intro: "Measures how competitive and accessible this vehicle is for independent dealers.",
    signalsUsed: ["Active-to-sold ratio", "Days supply", "Franchise vs independent mix", "Stale inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Good availability" },
      { grades: "C", desc: "Tight availability" },
      { grades: "D–F", desc: "Limited availability" },
    ],
  },
  mds: {
    intro: "Measures how challenging it is to source, price, and turn this vehicle profitably.",
    signalsUsed: ["Days supply", "Crowding ratio", "Franchise/independent mix", "Price volatility"],
    gradeMeaning: [
      { grades: "A–B", desc: "Easy to sell" },
      { grades: "C", desc: "Moderate" },
      { grades: "D–F", desc: "Hard or very hard" },
    ],
  },
  market_friction: {
    intro: "Measures how smooth or frustrating it is to price, manage, and exit this vehicle.",
    signalsUsed: ["Price dispersion", "DOM predictability", "Version fragmentation", "Long-tail inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Low friction" },
      { grades: "C", desc: "Moderate friction" },
      { grades: "D–F", desc: "High friction" },
    ],
  },
};

function InsightPopoverContent({ label, content }) {
  if (!content) return null;
  const popoverStyle = {
    background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
    borderRadius: 10,
    padding: "14px 16px",
    maxWidth: 320,
    border: "1px solid rgba(255,255,255,.06)",
    boxShadow: "0 10px 26px rgba(0,0,0,.35)",
  };
  const titleStyle = { color: "#ffffff", fontSize: 14, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 };
  const pStyle = { color: "#94a3b8", fontSize: 12, lineHeight: 1.5, marginBottom: 12 };
  const headingStyle = { color: "#ffffff", fontSize: 12, fontWeight: 600, marginBottom: 6 };
  const listStyle = { margin: 0, paddingLeft: 18, color: "#94a3b8", fontSize: 12, lineHeight: 1.6 };
  const bulletStyle = { position: "absolute", left: -10, top: 5, width: 5, height: 5, borderRadius: "50%", background: "#f59e0b" };
  const gradeItemStyle = { marginBottom: 4, color: "#94a3b8", fontSize: 12 };
  const gradeBoldStyle = { color: "#ffffff", fontWeight: 600 };
  return (
    <div style={popoverStyle}>
      <div style={titleStyle}>
        {label}
        <span style={{ color: "#64748b", fontSize: 12 }}>ⓘ</span>
      </div>
      <p style={pStyle}>{content.intro}</p>
      <div style={headingStyle}>Signals used:</div>
      <ul style={listStyle}>
        {content.signalsUsed.map((s, i) => (
          <li key={i} style={{ position: "relative", marginBottom: 4 }}>
            <span style={bulletStyle} />
            {s}
          </li>
        ))}
      </ul>
      <div style={{ ...headingStyle, marginTop: 12 }}>Grade meaning:</div>
      <ul style={{ ...listStyle, listStyle: "none", paddingLeft: 0 }}>
        {content.gradeMeaning.map((g, i) => (
          <li key={i} style={gradeItemStyle}>
            <span style={gradeBoldStyle}>{g.grades}</span> {g.desc}
          </li>
        ))}
      </ul>
    </div>
  );
}

const AI_INSIGHT_CONFIG = [
  { key: "buyer_demand", label: "Buyer Demand", scoreKey: "demand_score", gradeKey: "demand_grade" },
  { key: "interest", label: "Buyer Interest", scoreKey: "interest_score", gradeKey: "interest_grade" },
  { key: "volume", label: "Volume", scoreKey: "volume_score", gradeKey: "volume_grade" },
  { key: "availability", label: "Availability", scoreKey: "availability_score", gradeKey: "availability_grade" },
  { key: "mds", label: "MDS", scoreKey: "mds_score", gradeKey: "mds_grade" },
  { key: "market_friction", label: "Market Friction", scoreKey: "market_friction_score", gradeKey: "market_friction_grade" },
];

function gradeToColor(grade) {
  const g = String(grade ?? "").trim().toUpperCase();
  if (g.startsWith("A") || g.startsWith("B")) return { border: "#22c55e", glow: "rgba(34,197,94,0.5)", text: "#22c55e" };
  if (g.startsWith("C")) return { border: "#eab308", glow: "rgba(234,179,8,0.45)", text: "#eab308" };
  if (g.startsWith("D") || g === "F") return { border: "#ef4444", glow: "rgba(239,68,68,0.5)", text: "#ef4444" };
  return { border: "#94a3b8", glow: "rgba(148,163,184,0.4)", text: "#94a3b8" };
}

function formatReasonCode(code) {
  return (code || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function getAiMarketInsights(data) {
  const raw = data?.ai_market_insights ?? data?.market_analysis?.ai_market_insights;
  if (!raw) return null;
  if (Array.isArray(raw?.cards) && raw.cards.length > 0) return { source: "cards", data: raw.cards };
  const hasNewShape = AI_INSIGHT_CONFIG.some(({ key }) => raw[key] != null);
  if (hasNewShape) return { source: "object", data: raw };
  return null;
}

export default function MarketStrategyInsightsCard({ data, mobile }) {
  if (!data) return null;

  const insightsSource = getAiMarketInsights(data);
  const insights = useMemo(() => {
    if (!insightsSource) {
      return AI_INSIGHT_CONFIG.map((cfg, i) => ({
        id: `empty-${i}`,
        key: cfg.key,
        label: cfg.label,
        score: null,
        grade: null,
        confidence: null,
        reasonCodes: [],
        empty: true,
      }));
    }
    if (insightsSource.source === "object") {
      return AI_INSIGHT_CONFIG.map((cfg) => {
        const block = insightsSource.data[cfg.key];
        return {
          id: cfg.key,
          key: cfg.key,
          label: cfg.label,
          score: block?.[cfg.scoreKey],
          grade: block?.[cfg.gradeKey],
          confidence: block?.confidence,
          reasonCodes: block?.reason_codes || [],
          empty: !block,
        };
      });
    }
    return insightsSource.data.map((card, i) => ({
      id: card.id ?? `card-${i}`,
      key: card.key ?? card.id,
      label: card.title ?? card.label,
      score: card.score,
      grade: card.grade ?? card.pill_text,
      confidence: card.confidence,
      reasonCodes: card.reason_codes || [],
      empty: false,
    }));
  }, [insightsSource]);

  return (
    <div className={`msi-card msi-ai-grid ${mobile ? "msi-card-mobile" : ""}`}>
      {!mobile && (
        <div className="msi-header">
          <div className="msi-title">AI Market Insights</div>
          <div className="msi-subtitle">Based on recent market activity</div>
        </div>
      )}

      <div className={`msi-insights-grid ${mobile ? "msi-insights-grid-mobile" : ""}`}>
        {(mobile ? insights.filter((i) => i.key !== "market_friction") : insights).map((item) => {
          const colors = gradeToColor(item.grade);
          const g = String(item.grade ?? "").trim().toUpperCase();
          const bulletColor = !item.grade ? "var(--ms-muted)" : g.startsWith("D") || g === "F" ? "#ef4444" : g.startsWith("C") ? "#eab308" : "#22c55e";
          return (
            <div key={item.id} className="msi-insight-card msi-insight-card-new">
              <div className="msi-insight-card-left">
                <div className="msi-insight-title">
                  <span>{item.label}</span>
                  <Popover
                    content={<InsightPopoverContent label={item.label} content={AI_INSIGHT_POPOVER_CONTENT[item.key]} />}
                    overlayInnerStyle={{ background: "transparent", border: "none", boxShadow: "none", padding: 0 }}
                    trigger="hover"
                    placement="right"
                  >
                    <span className="msi-insight-info-wrap">
                      <InfoCircleOutlined className="msi-insight-info-icon" />
                    </span>
                  </Popover>
                </div>
                {item.reasonCodes?.length > 0 && (
                  <ul className="msi-insight-reasons" style={{ ["--bullet-color"]: bulletColor }}>
                    {item.reasonCodes.slice(0, 3).map((code, i) => (
                      <li key={i}>{formatReasonCode(code)}</li>
                    ))}
                  </ul>
                )}
              </div>
              {(item.grade != null && !item.empty) && (
                <div className="msi-insight-card-right-stack">
                  <div
                    className="msi-grade-circle"
                    style={{
                      borderColor: colors.border,
                      boxShadow: `0 0 14px ${colors.glow}`,
                      color: colors.text,
                    }}
                  >
                    {item.grade}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!mobile && (
        <div className="msi-footer">
          Insights are observational — not pricing guarantees.
        </div>
      )}
    </div>
  );
}
